import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';

/** Something that happened on a topic, from whichever transport carries it. */
export interface LiveEvent<T = unknown> {
  topic: string;
  payload: T;
  /** Epoch milliseconds when the event was produced, when the source knows. */
  at?: number;
}

/**
 * A push transport — SSE, WebSocket, a BroadcastChannel — reduced to one method so the screens never
 * know which. Without a source the app is polling-only, which `useLivePolling` covers.
 */
export interface LiveSource {
  subscribe(topic: string, callback: (event: LiveEvent) => void): () => void;
}

export interface MemoryLiveSource extends LiveSource {
  /** Delivers an event to every subscriber of `topic`. */
  emit<T>(topic: string, payload: T): void;
  /** How many subscribers a topic has, for assertions. */
  count(topic: string): number;
}

/** An in-memory source for tests and previews. */
export function createMemoryLiveSource(): MemoryLiveSource {
  const topics = new Map<string, Set<(event: LiveEvent) => void>>();
  return {
    subscribe: (topic, callback) => {
      let set = topics.get(topic);
      if (!set) {
        set = new Set();
        topics.set(topic, set);
      }
      set.add(callback);
      return () => {
        set?.delete(callback);
        if (set?.size === 0) topics.delete(topic);
      };
    },
    emit: (topic, payload) => {
      const event: LiveEvent = { topic, payload, at: Date.now() };
      topics.get(topic)?.forEach((callback) => callback(event));
    },
    count: (topic) => topics.get(topic)?.size ?? 0,
  };
}

export interface LiveUpdatesContextValue {
  source: LiveSource | undefined;
  /** The interval `useLivePolling` uses when it is not given one. */
  pollIntervalMs: number;
}

const LiveUpdatesContext = createContext<LiveUpdatesContextValue>({ source: undefined, pollIntervalMs: 60_000 });

export interface LiveUpdatesProviderProps {
  /** A push transport; leave it out for polling only. */
  source?: LiveSource;
  /** Default 60 000 ms. */
  pollIntervalMs?: number;
  children?: ReactNode;
}

export function LiveUpdatesProvider({ source, pollIntervalMs = 60_000, children }: LiveUpdatesProviderProps) {
  const value = useMemo<LiveUpdatesContextValue>(() => ({ source, pollIntervalMs }), [source, pollIntervalMs]);
  return <LiveUpdatesContext.Provider value={value}>{children}</LiveUpdatesContext.Provider>;
}

export function useLiveUpdatesContext(): LiveUpdatesContextValue {
  return useContext(LiveUpdatesContext);
}

export interface UseLiveUpdatesOptions {
  /** Default true. */
  enabled?: boolean;
}

export interface LiveUpdatesStatus {
  /** True while a push source is delivering this topic; false when the app is polling only. */
  live: boolean;
}

/**
 * Runs `handler` for every event on `topic` from the provider's source. The handler is read through a
 * ref, so passing a new closure each render does not resubscribe. With no source it does nothing and
 * reports `live: false`, which a screen uses to decide whether to poll instead.
 */
export function useLiveUpdates<T = unknown>(
  topic: string,
  handler: (payload: T, event: LiveEvent<T>) => void,
  options: UseLiveUpdatesOptions = {},
): LiveUpdatesStatus {
  const { source } = useLiveUpdatesContext();
  const { enabled = true } = options;
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    if (!source || !enabled) return;
    return source.subscribe(topic, (event) => {
      const typed = event as LiveEvent<T>;
      handlerRef.current(typed.payload, typed);
    });
  }, [source, topic, enabled]);
  return { live: Boolean(source) && enabled };
}

/**
 * Calls `refetch` every `intervalMs` while `enabled`, pausing while the tab is hidden and catching up
 * as soon as it is visible again if a tick was missed. `intervalMs` defaults to the provider's.
 */
export function useLivePolling(enabled: boolean, intervalMs: number | undefined, refetch: () => void): void {
  const { pollIntervalMs } = useLiveUpdatesContext();
  const interval = intervalMs ?? pollIntervalMs;
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    if (!enabled || interval <= 0 || typeof document === 'undefined') return;
    let timer: ReturnType<typeof setInterval> | undefined;
    let lastRun = Date.now();
    const tick = () => {
      lastRun = Date.now();
      refetchRef.current();
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(tick, interval);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = undefined;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
        return;
      }
      if (Date.now() - lastRun >= interval) tick();
      start();
    };
    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) start();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [enabled, interval]);
}
